/**
 * Попап МАСОВОГО швидкого створення номенклатури (2026-08-04, рішення
 * власника): з вхідної ПН — рядки предзаповнюються з «Опис» + УКТЗЕД
 * табличної частини; усі поля рядка в ОДИН рядок; ✕ прибирає зайві.
 * Так створюємо номенклатуру, якої у нас немає (або вона «не така», як
 * у постачальника). Код — авто (NOM-N), як у QuickNomenclatureModal.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { nomenclature as nomApi, type Nomenclature } from '@/api/catalogs';
import { refs } from '@/api/refs';

export interface BatchNomInitialRow {
  /** _key-і рядків документа, до яких привʼязати створену номенклатуру. */
  keys: string[];
  name: string;
  uktZed: string | null;
  vatRateId: string | null;
}

interface EditRow extends BatchNomInitialRow {
  kindId: string;
  article: string;
  error: string | null;
}

export default function QuickNomenclatureBatchModal({ initialRows, onCreated, onClose }: {
  initialRows: BatchNomInitialRow[];
  onCreated: (created: { keys: string[]; nom: Nomenclature }[]) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const kindsQ = useQuery({
    queryKey: ['nomenclature-kinds'],
    queryFn: refs.nomenclatureKinds.list,
  });
  const vatRatesQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const kinds = kindsQ.data ?? [];
  const goodsKindId = kinds.find((k) => k.code === 'GOODS')?.id ?? '';

  const [rows, setRows] = useState<EditRow[] | null>(null);
  // Дефолтний вид «Товар» підставляється коли довідник видів довантажився.
  if (rows === null && kindsQ.data) {
    setRows(initialRows.map((r) => ({
      ...r, kindId: goodsKindId, article: '', error: null,
    })));
  }
  const list = rows ?? [];

  const setRowAt = (i: number, patch: Partial<EditRow>) =>
    setRows((s) => (s ?? []).map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const create = useMutation({
    mutationFn: async () => {
      const created: { keys: string[]; nom: Nomenclature }[] = [];
      const failed: EditRow[] = [];
      for (const r of list) {
        const kind = kinds.find((k) => k.id === r.kindId);
        const isService = kind?.is_service ?? false;
        try {
          const nom = await nomApi.create({
            code: null,
            article: r.article.trim() || null,
            name: r.name.trim(),
            full_name: null,
            is_service: isService,
            is_excise: false,
            ukt_zed_code: (!isService && r.uktZed?.trim()) ? r.uktZed.trim() : null,
            country_origin_code: null,
            base_uom_id: null,
            vat_rate_id: r.vatRateId || null,
            default_cost_item_id: null,
            kind_id: r.kindId || null,
            account_id: null,
            transfer_account_id: null,
            off_balance_account_id: null,
            off_balance_transfer_account_id: null,
            tax_purpose_id: null,
            income_cost_tax_purpose_id: null,
          });
          created.push({ keys: r.keys, nom });
        } catch (e: any) {
          failed.push({
            ...r,
            error: e?.response?.data?.detail ?? e?.message ?? String(e),
          });
        }
      }
      return { created, failed };
    },
    onSuccess: ({ created, failed }) => {
      if (created.length) {
        qc.invalidateQueries({ queryKey: ['nomenclature-full'] });
        qc.invalidateQueries({ queryKey: ['nomenclature'] });
        onCreated(created);
      }
      if (failed.length) setRows(failed);
      else onClose();
    },
  });

  const canCreate = list.length > 0
    && list.every((r) => r.name.trim() && r.kindId)
    && !create.isPending;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div className="card" style={{ width: 980, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', margin: 0 }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Нова номенклатура з рядків ПН</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        {list.length === 0 && (
          <p style={{ color: 'var(--muted)' }}>
            Немає рядків для створення — усі рядки ПН вже привʼязані до номенклатури.
          </p>
        )}
        {list.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
                <th style={{ padding: '0.35rem', minWidth: 260 }}>
                  Найменування <span style={{ color: 'red' }}>*</span>
                </th>
                <th style={{ padding: '0.35rem', width: 150 }}>
                  Вид <span style={{ color: 'red' }}>*</span>
                </th>
                <th style={{ padding: '0.35rem', width: 140 }}>Ставка ПДВ</th>
                <th style={{ padding: '0.35rem', width: 110 }}>Артикул</th>
                <th style={{ padding: '0.35rem', width: 120 }}>Код УКТ ЗЕД</th>
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r, i) => {
                const isService = kinds.find((k) => k.id === r.kindId)?.is_service ?? false;
                return (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.3rem' }}>
                      <input value={r.name}
                        onChange={(e) => setRowAt(i, { name: e.target.value })} />
                      {r.error && (
                        <div style={{ color: '#dc2626', fontSize: '0.78rem' }}>{r.error}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.3rem' }}>
                      <select value={r.kindId}
                        onChange={(e) => setRowAt(i, { kindId: e.target.value })}>
                        <option value="">— оберіть —</option>
                        {kinds.map((k) => (
                          <option key={k.id} value={k.id}>{k.name}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '0.3rem' }}>
                      <select value={r.vatRateId ?? ''}
                        onChange={(e) => setRowAt(i, { vatRateId: e.target.value || null })}>
                        <option value="">— з документа —</option>
                        {(vatRatesQ.data ?? []).map((v) => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '0.3rem' }}>
                      <input value={r.article}
                        onChange={(e) => setRowAt(i, { article: e.target.value })} />
                    </td>
                    <td style={{ padding: '0.3rem' }}>
                      <input value={r.uktZed ?? ''} disabled={isService}
                        placeholder={isService ? '(послуга)' : '—'}
                        onChange={(e) => setRowAt(i, { uktZed: e.target.value || null })} />
                    </td>
                    <td style={{ padding: '0.3rem', textAlign: 'center' }}>
                      <span style={{ cursor: 'pointer', color: '#dc2626' }}
                        title="прибрати рядок (номенклатура НЕ створюється)"
                        onClick={() => setRows((s) => (s ?? []).filter((_, j) => j !== i))}>
                        ×
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0.5rem 0 0.6rem' }}>
          Код призначиться автоматично. Створена номенклатура одразу підставиться
          у відповідні рядки ПН. Одиниця виміру, рахунки обліку та інші реквізити —
          у повній картці номенклатури.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="primary" disabled={!canCreate}
            title={!list.length ? 'Немає рядків'
              : !list.every((r) => r.name.trim()) ? 'Вкажіть найменування у кожному рядку'
              : !list.every((r) => r.kindId) ? 'Оберіть вид номенклатури у кожному рядку'
              : undefined}
            onClick={() => create.mutate()}>
            {create.isPending ? 'створюється…'
              : `створити і підставити (${list.length})`}
          </button>
          <button onClick={onClose}>скасувати</button>
        </div>
      </div>
    </div>
  );
}
